<script>
import { mapGetters } from 'vuex';
import ConversationHeader from './ConversationHeader.vue';
import DashboardAppFrame from '../DashboardApp/Frame.vue';
import EmptyState from './EmptyState/EmptyState.vue';
import MessagesView from './MessagesView.vue';
import MiraStaffPanel from './MiraStaffPanel.vue';
import MiraAssistPanel from './MiraAssistPanel.vue';
import MiraStaffAPI from 'dashboard/api/miraStaff';

export default {
  components: {
    ConversationHeader,
    DashboardAppFrame,
    EmptyState,
    MessagesView,
    MiraStaffPanel,
    MiraAssistPanel,
  },
  props: {
    inboxId: {
      type: [Number, String],
      default: '',
      required: false,
    },
    isInboxView: {
      type: Boolean,
      default: false,
    },
    isContactPanelOpen: {
      type: Boolean,
      default: true,
    },
    isOnExpandedLayout: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    // miraEnabled: the Mira tab shows; miraPilot: the owner-pilot chat; miraSupport: drafts and summaries.
    return {
      activeIndex: 0,
      miraEnabled: false,
      miraPilot: false,
      miraSupport: false,
    };
  },
  computed: {
    ...mapGetters({
      currentChat: 'getSelectedChat',
      dashboardApps: 'dashboardApps/getRecords',
    }),
    dashboardAppTabs() {
      return [
        {
          key: 'messages',
          index: 0,
          name: this.$t('CONVERSATION.DASHBOARD_APP_TAB_MESSAGES'),
        },
        ...(this.miraEnabled
          ? [
              {
                key: 'mira',
                index: 1,
                name: this.$t('CONVERSATION.MIRA.TAB'),
              },
            ]
          : []),
        ...this.dashboardApps.map((dashboardApp, index) => ({
          key: `dashboard-${dashboardApp.id}`,
          index: index + (this.miraEnabled ? 2 : 1),
          name: dashboardApp.title,
        })),
      ];
    },
    showContactPanel() {
      return this.isContactPanelOpen && this.currentChat.id;
    },
  },
  watch: {
    'currentChat.inbox_id': {
      immediate: true,
      handler(inboxId) {
        if (inboxId) {
          this.$store.dispatch('inboxAssignableAgents/fetch', [inboxId]);
        }
      },
    },
    'currentChat.id'() {
      this.fetchLabels();
      this.activeIndex = 0;
    },
  },
  mounted() {
    this.fetchLabels();
    this.$store.dispatch('dashboardApps/get');
    this.fetchMiraStatus();
  },
  methods: {
    fetchLabels() {
      if (!this.currentChat.id) {
        return;
      }
      this.$store.dispatch('conversationLabels/get', this.currentChat.id);
    },
    onDashboardAppTabChange(index) {
      this.activeIndex = index;
    },
    async fetchMiraStatus() {
      try {
        const { data } = await MiraStaffAPI.status();
        this.activeIndex = 0;
        this.miraPilot = data.enabled === true;
        this.miraSupport = data.support === true;
        this.miraEnabled = this.miraPilot || this.miraSupport;
      } catch {
        this.activeIndex = 0;
        this.miraEnabled = false;
      }
    },
  },
};
</script>

<template>
  <div
    class="conversation-details-wrap flex flex-col min-w-0 w-full bg-n-surface-1 relative"
    :class="{
      'border-l rtl:border-l-0 rtl:border-r border-n-weak': !isOnExpandedLayout,
    }"
  >
    <ConversationHeader
      v-if="currentChat.id"
      :chat="currentChat"
      :show-back-button="isOnExpandedLayout && !isInboxView"
      :class="{
        'border-b border-b-n-weak !pt-2': !dashboardApps.length && !miraEnabled,
      }"
    />
    <woot-tabs
      v-if="(dashboardApps.length || miraEnabled) && currentChat.id"
      :index="activeIndex"
      class="h-10"
      @change="onDashboardAppTabChange"
    >
      <woot-tabs-item
        v-for="tab in dashboardAppTabs"
        :key="tab.key"
        :index="tab.index"
        :name="tab.name"
        :show-badge="false"
        is-compact
      />
    </woot-tabs>
    <div v-show="!activeIndex" class="flex h-full min-h-0 m-0">
      <MessagesView
        v-if="currentChat.id"
        :inbox-id="inboxId"
        :is-inbox-view="isInboxView"
      />
      <EmptyState
        v-if="!currentChat.id && !isInboxView"
        :is-on-expanded-layout="isOnExpandedLayout"
      />
      <slot />
    </div>
    <div
      v-if="miraEnabled && currentChat.id"
      v-show="activeIndex === 1"
      :key="`mira-${currentChat.id}`"
      class="flex flex-col flex-1 min-h-0 overflow-y-auto bg-n-surface-1"
    >
      <MiraAssistPanel v-if="miraSupport" :conversation-id="currentChat.id" />
      <MiraStaffPanel v-if="miraPilot" :conversation-id="currentChat.id" />
    </div>
    <DashboardAppFrame
      v-for="(dashboardApp, index) in dashboardApps"
      v-show="activeIndex - (miraEnabled ? 2 : 1) === index"
      :key="currentChat.id + '-' + dashboardApp.id"
      :is-visible="activeIndex - (miraEnabled ? 2 : 1) === index"
      :config="dashboardApps[index].content"
      :position="index"
      :current-chat="currentChat"
    />
  </div>
</template>
